import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { ImageSizeHelper } from '@tribeplatform/react-components/Picker'
import { useRouter } from '@tribeplatform/react-sdk'
import { useAuthToken } from '@tribeplatform/react-sdk/hooks'
import { Tabs } from '@tribeplatform/react-ui-kit/Tabs'

import { useCustomizerLogos } from '../hooks/use-customizer-logos.hook.js'
import { useCustomizerPreview } from '../hooks/use-customizer-preview.hook.js'
import { LogoPicker } from './components/LogoPicker.js'

export const AppearanceLogos = () => {
  const { $t } = useI18n()
  const { Link } = useRouter()
  const { previewDarkMode, setPreviewDarkMode, hasDarkMode } =
    useCustomizerPreview()

  const { imageIds, updateImageIds } = useCustomizerLogos()

  const { data: authToken } = useAuthToken()
  const { networkPublicInfo } = authToken || {}
  const fallbackLogo = networkPublicInfo.logo
  const fallbackMobileLogo = networkPublicInfo.favicon
  const fallbackFavicon = networkPublicInfo.favicon

  const index = previewDarkMode ? 1 : 0

  return (
    <Tabs.Group
      defaultIndex={hasDarkMode ? undefined : index}
      selectedIndex={hasDarkMode ? index : undefined}
      onChange={index => {
        if (hasDarkMode) {
          setPreviewDarkMode(index === 1)
        }
      }}
    >
      <Tabs.List variant="plain" fullWidth>
        <Tabs.Tab className="whitespace-nowrap" name="light">
          <T id="Admin.Appearance.LightLogo" defaultMessage="Light logo" />
        </Tabs.Tab>
        <Tabs.Tab className="whitespace-nowrap" name="dark">
          <T id="Admin.Appearance.DarkLogo" defaultMessage="Dark logo" />
        </Tabs.Tab>
      </Tabs.List>
      <div className="text-sm pt-2">
        <T
          defaultMessage="For optimal logo display in the header, select the appropriate size (small, medium, large) from the <link>Design Studio</link>."
          id="Admin.Appearance.LogosDescription"
          values={{
            link: label => (
              <Link href="/manage/customizer/navigation" variant="accent">
                {label}
              </Link>
            ),
          }}
        />
      </div>
      <Tabs.Panels>
        <Tabs.Panel>
          <div className="flex flex-col gap-6 pt-4">
            <LogoPicker
              label={$t({
                defaultMessage: 'Light logo image',
                id: 'Admin.Appearance.LightLogoImage',
              })}
              imageId={imageIds?.lightLogoId ?? fallbackLogo?.id}
              onChange={imageId => {
                updateImageIds({
                  lightLogoId: imageId,
                  darkLogoId: imageIds?.darkLogoId ?? imageId,
                })
              }}
              helperText={
                <ImageSizeHelper
                  recommendedWidth={600}
                  recommendedHeight={80}
                  showSizeLimit={false}
                />
              }
            />

            <LogoPicker
              label={$t({
                defaultMessage: 'Light square image',
                id: 'Admin.Appearance.LightSquareImage',
              })}
              imageId={imageIds?.lightMobileLogoId ?? fallbackMobileLogo?.id}
              onChange={imageId => {
                updateImageIds({
                  lightMobileLogoId: imageId,
                  lightFaviconId: imageIds?.lightFaviconId ?? imageId,
                  darkMobileLogoId: imageIds?.darkMobileLogoId ?? imageId,
                  darkFaviconId: imageIds?.darkFaviconId ?? imageId,
                })
              }}
              helperText={
                <ImageSizeHelper
                  recommendedWidth={512}
                  recommendedHeight={512}
                  showSizeLimit={false}
                />
              }
            />

            <LogoPicker
              label={$t({
                defaultMessage: 'Light favicon',
                id: 'Admin.Appearance.LightFavicon',
              })}
              imageId={imageIds?.lightFaviconId ?? fallbackFavicon?.id}
              onChange={imageId => {
                updateImageIds({
                  lightFaviconId: imageId,
                  darkFaviconId: imageIds?.darkFaviconId ?? imageId,
                })
              }}
              helperText={
                <ImageSizeHelper
                  recommendedWidth={512}
                  recommendedHeight={512}
                  showSizeLimit={false}
                />
              }
            />
          </div>
        </Tabs.Panel>
        <Tabs.Panel>
          <div className="flex flex-col gap-6 pt-4">
            <LogoPicker
              darkMode
              label={$t({
                defaultMessage: 'Dark logo image',
                id: 'Admin.Appearance.DarkLogoImage',
              })}
              imageId={imageIds?.darkLogoId ?? fallbackLogo?.id}
              onChange={imageId => {
                updateImageIds({
                  darkLogoId: imageId,
                })
              }}
              helperText={
                <ImageSizeHelper
                  recommendedWidth={600}
                  recommendedHeight={80}
                  showSizeLimit={false}
                />
              }
            />

            <LogoPicker
              darkMode
              label={$t({
                defaultMessage: 'Dark square image',
                id: 'Admin.Appearance.DarkSquareImage',
              })}
              imageId={imageIds?.darkMobileLogoId ?? fallbackMobileLogo?.id}
              onChange={imageId => {
                updateImageIds({
                  darkMobileLogoId: imageId,
                })
              }}
              helperText={
                <ImageSizeHelper
                  recommendedWidth={512}
                  recommendedHeight={512}
                  showSizeLimit={false}
                />
              }
            />

            <LogoPicker
              darkMode
              label={$t({
                defaultMessage: 'Dark favicon',
                id: 'Admin.Appearance.DarkFavicon',
              })}
              imageId={imageIds?.darkFaviconId ?? fallbackFavicon?.id}
              onChange={imageId => {
                updateImageIds({
                  darkFaviconId: imageId,
                })
              }}
              helperText={
                <ImageSizeHelper
                  recommendedWidth={512}
                  recommendedHeight={512}
                  showSizeLimit={false}
                />
              }
            />
          </div>
        </Tabs.Panel>
      </Tabs.Panels>
    </Tabs.Group>
  )
}
